import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { getReadableTextColour } from '@payaca/utilities/colourUtilities';
import React, { FunctionComponent, type JSX } from 'react';
import './Button.sass';
import { ButtonColourVariant, ButtonStyleVariant } from './enums';

export type Props = {
  isDisabled?: boolean;
  iconBefore?: TIconName | JSX.Element;
  iconAfter?: TIconName | JSX.Element;
  onClick?: () => void;
  onClickDisabled?: () => void;
  isProcessing?: boolean;
  styleVariant?: ButtonStyleVariant;
  colourVariant?: ButtonColourVariant;
  customColour?: string;
  hasBoxShadow?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  isOutlined?: boolean;
};

const Button: FunctionComponent<React.PropsWithChildren<Props>> = ({
  isDisabled = false,
  children,
  iconBefore,
  iconAfter,
  onClick,
  onClickDisabled,
  isProcessing = false,
  styleVariant = ButtonStyleVariant.STANDARD,
  colourVariant,
  customColour,
  hasBoxShadow = false,
  type = 'button',
  className,
  isOutlined = false,
}: React.PropsWithChildren<Props>): JSX.Element => {
  const internalColourVariant =
    colourVariant ||
    (styleVariant === ButtonStyleVariant.ANCHOR
      ? ButtonColourVariant.MIDBLUE
      : ButtonColourVariant.SECONDARY);

  return (
    <button
      className={`${
        className ? `${className} ` : ''
      }button v2 button-style-${styleVariant}${
        !customColour ? ` button-col-${internalColourVariant}` : ''
      }${isDisabled ? ' disabled' : ''}${isProcessing ? ' processing' : ''}${
        hasBoxShadow ? ' box-shadow' : ''
      }${isOutlined ? ' outlined' : ''}`}
      onClick={isDisabled ? onClickDisabled : onClick}
      disabled={isDisabled && !onClickDisabled}
      type={type}
      style={{
        backgroundColor: customColour,
        color: customColour ? getReadableTextColour(customColour) : undefined,
      }}
    >
      <React.Fragment>
        {iconBefore && (
          <span className="icon">
            {React.isValidElement(iconBefore) ? (
              iconBefore
            ) : (
              // @ts-expect-error
              <UntitledIcon name={iconBefore} />
            )}
          </span>
        )}
        {isProcessing && (
          <span className={'spinner-container'}>
            <UntitledIcon name="loading-01.3" className="animate-spin" />
          </span>
        )}
        <span className={`button-content`}>{children}</span>
        {iconAfter && (
          <span className="icon">
            {React.isValidElement(iconAfter) ? (
              iconAfter
            ) : (
              // @ts-expect-error
              <UntitledIcon name={iconAfter} />
            )}
          </span>
        )}
      </React.Fragment>
    </button>
  );
};

export default Button;
